import { call, fork, put, SagaReturnType, select, take } from "redux-saga/effects";
import slice from "./reducer";

import backOfficeApplicationsApi from "../../api/apiServices/backOffice/controllers/applications/applications.api";
import {
  makeSelectAuthenticatedUserInfo,
  makeSelectBitrixOptions,
  makeSelectTransportCharacteristics,
} from "../../containers/App/selectors";
import { isInternalOffice } from "../../utils/isInternalOffice";
import { history } from "../../history";
import { makeSelectLeadId } from "./selectors";

function* getBitrixLeadId() {
  const user: ReturnType<ReturnType<typeof makeSelectAuthenticatedUserInfo>> = yield select(makeSelectAuthenticatedUserInfo());
  const options: ReturnType<ReturnType<typeof makeSelectBitrixOptions>> = yield select(makeSelectBitrixOptions());
  return isInternalOffice(user?.company.settings.officeType) ? undefined : options?.leadId;
}

function* createFtlApplicationWatcher() {
  while (true) {
    const { payload }: ReturnType<typeof slice.actions.createFtlApplication> = yield take(slice.actions.createFtlApplication.type);

    const bitrixLeadId: SagaReturnType<typeof getBitrixLeadId> = yield call(getBitrixLeadId);
    const leadId: ReturnType<typeof makeSelectLeadId> = yield select(makeSelectLeadId);

    const { success, reject }: SagaReturnType<typeof backOfficeApplicationsApi.create> = yield call(backOfficeApplicationsApi.create, {
      companyId: payload.companyId,
      bitrixLeadId,
      filter: {
        fromId: payload.fromLocation.id,
        toId: payload.toLocation.id,
        waypointId: payload.waypointLocationId,
        date: payload.fromDate ? payload.fromDate.format("YYYY-MM-DD") : undefined,
        transportCharacteristicId: payload.transportCharacteristicId,
        count: payload.count,
        cost: payload.cost,
        currencyId: payload.currencyId,
      },
      lead: leadId ? { id: leadId } : undefined,
      version: 2,
      selectedOptions: ["CARGO_INSURANCE_NO"],
      cargoInfo: {
        dangerousCargoInfo: {
          isDangerous: false,
        },
        isRefrigeratorRequired: false,
        cargoDimensionsMode: "FROM_FILE",
        cargoCharacteristics: [],
      },
      oversize: false,
      needsSelectedOptions: false,
    });

    if (success) {
      yield put(slice.actions.createApplicationSuccess());
      history.push(`/applications/${success.payload.id}`);
    }

    if (reject) {
      yield put(slice.actions.createApplicationReject());
    }
  }
}

function* createFclApplicationWatcher() {
  while (true) {
    const { payload }: ReturnType<typeof slice.actions.createFclApplication> = yield take(slice.actions.createFclApplication.type);

    const bitrixLeadId: SagaReturnType<typeof getBitrixLeadId> = yield call(getBitrixLeadId);
    const leadId: ReturnType<typeof makeSelectLeadId> = yield select(makeSelectLeadId);

    const transportCharacteristics: ReturnType<ReturnType<typeof makeSelectTransportCharacteristics>> = yield select(
      makeSelectTransportCharacteristics(),
    );

    const transportCharacteristic = transportCharacteristics.find((x) => x.loadType === "FCL")!;

    const { success, reject }: SagaReturnType<typeof backOfficeApplicationsApi.create> = yield call(backOfficeApplicationsApi.create, {
      companyId: payload.companyId,
      bitrixLeadId,
      filter: {
        fromId: payload.fromLocation.id,
        toId: payload.toLocation.id,
        waypointId: payload.waypointLocationId,
        date: payload.fromDate ? payload.fromDate.format("YYYY-MM-DD") : undefined,
        transportCharacteristicId: payload.transportCharacteristicId,
        transportCharacteristicValue: transportCharacteristic.possibleValues?.find((x) => x.id === payload.transportCharacteristicValue)
          ?.containerType!,
        count: payload.count,
        cost: payload.cost,
        currencyId: payload.currencyId,
      },
      lead: leadId ? { id: leadId } : undefined,
      version: 1,
      selectedOptions: [],
      cargoInfo: {
        dangerousCargoInfo: {
          isDangerous: false,
        },
        cargoDimensionsMode: "ENTIRE_CARGO",
        cargoCharacteristics: [{ count: 1 }],
      },
    });

    if (success) {
      yield put(slice.actions.createApplicationSuccess());
      history.push(`/applications/${success.payload.id}`);
    }

    if (reject) {
      yield put(slice.actions.createApplicationReject());
    }
  }
}

function* createApplicationWatcher() {
  while (true) {
    const { payload }: ReturnType<typeof slice.actions.createApplication> = yield take(slice.actions.createApplication.type);

    const bitrixLeadId: SagaReturnType<typeof getBitrixLeadId> = yield call(getBitrixLeadId);
    const leadId: ReturnType<typeof makeSelectLeadId> = yield select(makeSelectLeadId);

    const { success, reject }: SagaReturnType<typeof backOfficeApplicationsApi.create> = yield call(backOfficeApplicationsApi.create, {
      companyId: payload.companyId,
      bitrixLeadId,
      filter: {
        transportCharacteristicId: payload.transportCharacteristicId,
        currencyId: payload.currencyId,
      },
      lead: leadId ? { id: leadId } : undefined,
      version: 1,
      selectedOptions: [],
      cargoInfo: {
        dangerousCargoInfo: {
          isDangerous: false,
        },
        cargoDimensionsMode: "ENTIRE_CARGO",
        cargoCharacteristics: [{ count: 1 }],
      },
    });

    if (success) {
      yield put(slice.actions.createApplicationSuccess());
      history.push(`/applications/${success.payload.id}`);
    }

    if (reject) {
      yield put(slice.actions.createApplicationReject());
    }
  }
}

export default function* createApplicationModalSaga() {
  yield fork(createFtlApplicationWatcher);
  yield fork(createFclApplicationWatcher);
  yield fork(createApplicationWatcher);
}
